import { type CSSProperties } from 'react';
import { cx } from '../../utils/classnames';
import { OnScreenKeyboard } from '../../components/OnScreenKeyboard';
import type { BoardProps, InputKey } from '../../engine/types';
import { isGiven, type LatticeMove, type LatticeState } from './logic';

export function LatticeBoard({ state, status, dispatch, feedback }: BoardProps<LatticeState, LatticeMove>) {
  const playing = status === 'playing';
  const lastError = feedback?.tone === 'error' ? feedback.nonce : 0;
  const left = state.maxAttempts - state.attempts;

  const onKey = (key: InputKey) => {
    if (key.type === 'char') dispatch({ type: 'set', ch: key.value });
    else if (key.type === 'backspace') dispatch({ type: 'remove' });
    else dispatch({ type: 'submit' });
  };

  return (
    <div className="cv-board">
      <div className="cv-lattice">
        <p className="cv-faint cv-mono" style={{ textAlign: 'center', fontSize: 'var(--text-sm)' }}>
          {left} {left === 1 ? 'try' : 'tries'} left
        </p>
        <div
          key={`grid-${lastError}`}
          className={cx('cv-lattice__grid', lastError && 'cv-anim-shake')}
          style={{ '--cols': state.size } as CSSProperties}
          role="group"
          aria-label="Word grid"
        >
          {state.cells.map((ch, i) => {
            const given = isGiven(state, i);
            return (
              <button
                key={i}
                type="button"
                className="cv-lattice__cell"
                data-given={given ? 'true' : undefined}
                data-cursor={state.cursor === i && playing ? 'true' : undefined}
                onClick={() => {
                  if (playing && !given) dispatch({ type: 'move', index: i });
                }}
                disabled={!playing || given}
                aria-label={`Row ${Math.floor(i / state.size) + 1}, column ${(i % state.size) + 1}${
                  given ? ', given' : ''
                }, ${ch ? ch.toUpperCase() : 'empty'}`}
              >
                {ch.toUpperCase()}
              </button>
            );
          })}
        </div>

        <div
          className="cv-board__status"
          data-tone={feedback?.tone === 'error' ? 'error' : undefined}
          aria-hidden="true"
        >
          {feedback?.message ?? ''}
        </div>
        <p className="visually-hidden" aria-live="polite">
          {feedback?.message}
        </p>

        <OnScreenKeyboard onKey={onKey} disabled={!playing} />
      </div>
    </div>
  );
}
